import { useCallback, useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Search, SlidersHorizontal, Sparkles, UserCheck, X, Download, Zap, Loader2, Clock,
} from "lucide-react";
import DrugDetailSheet from "@/components/DrugDetailSheet";
import ExternalSearchDialog from "@/components/ExternalSearchDialog";
import DataSourcesDialog from "@/components/DataSourcesDialog";
import TrendIndicator from "@/components/TrendIndicator";
import { formatGbp, formatRelative } from "@/lib/format";
import { toast } from "sonner";

const STAGE_BADGE = {
    "Preclinical": "bg-[#F1F5F9] text-[#475569]",
    "Phase I": "bg-[#F1F5F9] text-[#475569]",
    "Phase II": "bg-[#FEF3C7] text-[#92400E]",
    "Phase III": "bg-[#FED7AA] text-[#9A3412]",
    "Pre-registration": "bg-[#FFEDD5] text-[#C2410C]",
    "Approved": "bg-[#DCFCE7] text-[#166534]",
    "Marketed": "bg-[#1A4331] text-white",
};

function spendPill(gbp) {
    if (gbp == null) return <span className="text-xs text-[#8A9A86]">—</span>;
    let cls = "bg-[#F1F5F9] text-[#475569]";
    if (gbp >= 1_000_000) cls = "bg-[#C0604A] text-white";
    else if (gbp >= 500_000) cls = "bg-[#FED7AA] text-[#9A3412]";
    else if (gbp >= 100_000) cls = "bg-[#FEF3C7] text-[#92400E]";
    return <span className={`inline-flex items-center justify-center min-w-[52px] px-2 py-0.5 rounded-md text-xs font-mono font-medium ${cls}`}>{formatGbp(gbp)}</span>;
}

export default function DrugsListPage() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [drugs, setDrugs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [clinicalArea, setClinicalArea] = useState("all");
    const [stage, setStage] = useState("all");
    const [assigned, setAssigned] = useState("all");
    const [activeId, setActiveId] = useState(null);
    const [bulkRunning, setBulkRunning] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        const params = {};
        if (search) params.search = search;
        if (clinicalArea !== "all") params.clinical_area = clinicalArea;
        if (stage !== "all") params.development_stage = stage;
        if (assigned !== "all") params.assigned = assigned;
        api.get("/drugs", { params })
            .then((r) => setDrugs(r.data))
            .finally(() => setLoading(false));
    }, [search, clinicalArea, stage, assigned]);

    useEffect(() => {
        const t = setTimeout(load, 250);
        return () => clearTimeout(t);
    }, [load]);

    const areas = useMemo(() => {
        const s = new Set(drugs.map((d) => d.clinical_area));
        return ["all", ...Array.from(s).sort()];
    }, [drugs]);

    const stages = [
        "all", "Preclinical", "Phase I", "Phase II", "Phase III",
        "Pre-registration", "Approved", "Marketed",
    ];

    const clearFilters = () => { setSearch(""); setClinicalArea("all"); setStage("all"); setAssigned("all"); };
    const hasFilters = search || clinicalArea !== "all" || stage !== "all" || assigned !== "all";

    const exportCsv = async () => {
        const token = localStorage.getItem("rcn_token");
        const params = new URLSearchParams();
        if (search) params.set("search", search);
        if (clinicalArea !== "all") params.set("clinical_area", clinicalArea);
        if (stage !== "all") params.set("development_stage", stage);
        if (assigned !== "all") params.set("assigned", assigned);
        try {
            const res = await fetch(`${API}/exports/drugs.csv?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Export failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "rcn_pharma_opportunities.csv";
            a.click();
            URL.revokeObjectURL(url);
            toast.success("CSV exported");
        } catch (e) {
            toast.error("Export failed");
        }
    };

    const runBulk = async () => {
        setBulkRunning(true);
        try {
            const { data } = await api.post("/intelligence/bulk", {
                limit: 5,
                clinical_area: clinicalArea,
                development_stage: stage,
            });
            if (data.succeeded > 0) toast.success(`Generated intelligence for ${data.succeeded} drugs`);
            if (data.failed > 0) toast.error(`${data.failed} failed — check budget`);
            if (data.requested === 0) toast("All matching drugs already analysed");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk generation failed");
        } finally { setBulkRunning(false); }
    };

    return (
        <div className="p-6 lg:p-10 max-w-[1500px]">
            <div className="mb-8 fade-in-up">
                <p className="text-xs uppercase tracking-widest text-[#8A9A86]">Catalogue</p>
                <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#1A4331] mt-2">Drug Intelligence</h1>
                <p className="text-[#475569] mt-3 max-w-2xl">
                    UK pharmaceutical pipeline and approved products. Filter first to narrow to a manageable set, then generate AI spend analysis or export to CSV.
                </p>
            </div>

            {/* Filters */}
            <Card className="p-5 bg-white border-[#E2E8F0] mb-4 fade-in-up stagger-1" data-testid="filters-card">
                <div className="flex items-center gap-2 mb-4">
                    <SlidersHorizontal className="w-4 h-4 text-[#1A4331]" />
                    <p className="text-sm font-medium text-[#1A4331]">Filters</p>
                    {hasFilters && (
                        <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto text-[#475569] text-xs h-7" data-testid="clear-filters-btn">
                            <X className="w-3 h-3 mr-1" /> Clear
                        </Button>
                    )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8A9A86]" />
                        <Input placeholder="Search drug, company, indication…" value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9 h-10 bg-white border-[#E2E8F0]" data-testid="search-input" />
                    </div>
                    <Select value={clinicalArea} onValueChange={setClinicalArea}>
                        <SelectTrigger className="h-10 bg-white border-[#E2E8F0]" data-testid="clinical-area-select">
                            <SelectValue placeholder="Clinical area" />
                        </SelectTrigger>
                        <SelectContent>
                            {areas.map((a) => <SelectItem key={a} value={a}>{a === "all" ? "All clinical areas" : a}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={stage} onValueChange={setStage}>
                        <SelectTrigger className="h-10 bg-white border-[#E2E8F0]" data-testid="stage-select">
                            <SelectValue placeholder="Stage" />
                        </SelectTrigger>
                        <SelectContent>
                            {stages.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All stages" : s}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={assigned} onValueChange={setAssigned}>
                        <SelectTrigger className="h-10 bg-white border-[#E2E8F0]" data-testid="assigned-select">
                            <SelectValue placeholder="Assignment" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All assignments</SelectItem>
                            <SelectItem value="yes">Assigned</SelectItem>
                            <SelectItem value="no">Unassigned</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </Card>

            {/* Toolbar */}
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <p className="text-sm text-[#475569]" data-testid="results-count">
                    {loading ? "Loading…" : `${drugs.length} drug${drugs.length === 1 ? "" : "s"}`}
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button onClick={runBulk} disabled={bulkRunning} variant="outline"
                        className="gap-2 border-[#E2E8F0] bg-white" data-testid="bulk-generate-btn">
                        {bulkRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                        Bulk analyse (top 5)
                    </Button>
                    <Button onClick={exportCsv} variant="outline" className="gap-2 border-[#E2E8F0] bg-white" data-testid="export-csv-btn">
                        <Download className="w-4 h-4" /> Export CSV
                    </Button>
                    <DataSourcesDialog onRefreshed={load} />
                    {isAdmin && <ExternalSearchDialog onImported={load} />}
                </div>
            </div>

            {/* Table */}
            <Card className="bg-white border-[#E2E8F0] overflow-hidden fade-in-up stagger-2">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="drugs-table">
                        <thead className="bg-[#F8F7F4] border-b border-[#E2E8F0]">
                            <tr className="text-left">
                                <th className="px-4 py-3 font-medium text-[#475569] text-xs uppercase tracking-wider">Drug</th>
                                <th className="px-4 py-3 font-medium text-[#475569] text-xs uppercase tracking-wider">Company</th>
                                <th className="px-4 py-3 font-medium text-[#475569] text-xs uppercase tracking-wider">Stage</th>
                                <th className="px-4 py-3 font-medium text-[#475569] text-xs uppercase tracking-wider">Clinical area</th>
                                <th className="px-4 py-3 font-medium text-[#475569] text-xs uppercase tracking-wider text-center" title="Estimated annual £ spend on medical affairs to UK nursing market — next 12 months">Med Aff £</th>
                                <th className="px-4 py-3 font-medium text-[#475569] text-xs uppercase tracking-wider text-center" title="Estimated annual £ spend on drug promotion to UK nursing market — next 12 months">Promo £</th>
                                <th className="px-4 py-3 font-medium text-[#475569] text-xs uppercase tracking-wider">Assigned</th>
                                <th className="px-4 py-3 font-medium text-[#475569] text-xs uppercase tracking-wider">Updated</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!loading && drugs.length === 0 && (
                                <tr><td colSpan={8} className="px-5 py-16 text-center text-[#8A9A86]">No drugs match your filters.</td></tr>
                            )}
                            {drugs.map((d) => (
                                <tr key={d.id} onClick={() => setActiveId(d.id)}
                                    className="border-b border-[#E2E8F0] last:border-0 hover:bg-[#F8F7F4] cursor-pointer transition-colors"
                                    data-testid={`drug-row-${d.id}`}>
                                    <td className="px-4 py-3.5">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <div>
                                                <p className="font-medium text-[#1E293B]">{d.name}</p>
                                                <p className="text-xs text-[#475569]">{d.generic_name || d.indication}</p>
                                            </div>
                                            {(d.tags || []).includes("auto-discovered") && (
                                                <Badge className="bg-[#FEF3C7] text-[#92400E] hover:bg-[#FEF3C7] border-0 text-[10px]" title="Auto-discovered from ClinicalTrials.gov during a refresh run">
                                                    Auto
                                                </Badge>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3.5 text-[#1E293B]">{d.company}</td>
                                    <td className="px-4 py-3.5">
                                        <Badge className={`${STAGE_BADGE[d.development_stage] || "bg-[#F1F5F9]"} border-0 hover:opacity-90`}>
                                            {d.development_stage}
                                        </Badge>
                                    </td>
                                    <td className="px-4 py-3.5 text-[#475569]">{d.clinical_area}</td>
                                    <td className="px-4 py-3.5">
                                        <div className="flex items-center gap-1.5 justify-center">
                                            {spendPill(d.medical_affairs_spend_gbp)}
                                            <TrendIndicator trend={d.medical_affairs_trend} />
                                        </div>
                                    </td>
                                    <td className="px-4 py-3.5">
                                        <div className="flex items-center gap-1.5 justify-center">
                                            {spendPill(d.promotion_spend_gbp)}
                                            <TrendIndicator trend={d.promotion_trend} />
                                        </div>
                                    </td>
                                    <td className="px-4 py-3.5">
                                        {d.assignment ? (
                                            <span className="inline-flex items-center gap-1.5 text-xs text-[#166534]">
                                                <UserCheck className="w-3.5 h-3.5" />
                                                {d.assignment.salesperson_name}
                                            </span>
                                        ) : d.intelligence_generated ? (
                                            <span className="inline-flex items-center gap-1.5 text-xs text-[#8A9A86]">
                                                <Sparkles className="w-3.5 h-3.5" /> Analysed
                                            </span>
                                        ) : (
                                            <span className="text-xs text-[#8A9A86]">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3.5 text-xs text-[#475569]" title={d.last_updated || ""}>
                                        <span className="inline-flex items-center gap-1">
                                            <Clock className="w-3 h-3 text-[#8A9A86]" />
                                            {formatRelative(d.last_updated)}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            <DrugDetailSheet drugId={activeId} onClose={() => setActiveId(null)} onChanged={load} />
        </div>
    );
}
